<script setup>
import { ref, watch } from 'vue'

import { chatFormModeEnum, useChatStore } from '@/stores/chat.store'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { searchResultTypeEnum } from '@/stores/web-site.store'
import { useDetectDevice } from '@/composables/device'

import StaffEdit from '@/components/shared/StaffEdit.vue'
import EquipmentEdit from '@/components/shared/EquipmentEdit.vue'
import GroupEdit from '@/components/shared/GroupEdit.vue'
import CompanyEdit from '@/components/shared/CompanyEdit.vue'
import ProjectEdit from '@/components/shared/ProjectEdit.vue'
import BudgetDocumentEdit from '@/components/accounting/BudgetDocumentEdit.vue'
import CostReportRowEdit from '@/components/accounting/CostReportRowEdit.vue'

const chatStore = useChatStore()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const { isMobile } = useDetectDevice()

const editIdItem = async(id) => ({ id })

const expenseFormAsync = async(prefill) => {
  const project = prefill.project ? (await planningStore.fetchProjectByIdAsync(prefill.project)).data : null
  const photo = chatStore.latestPhoto()
  return {
    component: CostReportRowEdit,
    props: { project },
    item: { type: accountingStore.costReportRowTypeEnum.EXPENSE, quantity: 1, ...prefill, row_files: photo ? [photo] : [] },
  }
}

const entityForms = Object.freeze({
  [searchResultTypeEnum.STAFF]: { component: StaffEdit, loadAsync: editIdItem },
  [searchResultTypeEnum.EQUIPMENT]: { component: EquipmentEdit, loadAsync: editIdItem },
  [searchResultTypeEnum.GROUP]: { component: GroupEdit, loadAsync: editIdItem },
  [searchResultTypeEnum.COMPANY]: { component: CompanyEdit, loadAsync: editIdItem },
  [searchResultTypeEnum.PROJECT]: { component: ProjectEdit, loadAsync: async(id) => (await planningStore.fetchProjectByIdAsync(id)).data },
  [searchResultTypeEnum.QUOTE]: { component: BudgetDocumentEdit, loadAsync: async(id) => (await accountingStore.api.getBudgetDocumentByIdAsync(id)).data },
  [searchResultTypeEnum.EXPENSE]: { createAsync: expenseFormAsync },
})

const form = ref(null)

const openAsync = async(request) => {
  const config = entityForms[request.entity_type]
  const prefill = request.prefill ?? {}
  if (request.mode === chatFormModeEnum.EDIT) {
    form.value = { component: config.component, item: await config.loadAsync(request.entity_id), props: { changes: prefill } }
    return
  }
  form.value = config.createAsync ? await config.createAsync(prefill) : { component: config.component, item: prefill }
}

watch(() => chatStore.formRequest, async(request) => {
  if (!request) return
  chatStore.formRequest = null
  await openAsync(request)
})
</script>

<template>
  <q-dialog
    :model-value="form !== null"
    :maximized="isMobile"
    class="dialog-sm"
    @hide="form = null"
  >
    <component
      :is="form.component"
      v-if="form"
      v-bind="form.props"
      :model-value="form.item"
      @update="form = null"
      @cancel="form = null"
      @delete="form = null"
    />
  </q-dialog>
</template>
